import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assistantApi, subscriptionApi, type OrbNoticeChannel, type OrbNoticeState } from '@/api/client';

/****************
 * The Orb asks how to send its one notice. 2026-10-15.
 *
 * Guillaume: when the Orb finds a problem on someone's account and they have
 * not seen its message within a day, CopyFi sends ONE notice -- and "the Orb
 * ASK THE USER if he want telegram or mail notification". So this is the
 * Orb's own message, with three answers: email (what happens when nobody has
 * answered), Telegram, or only here in the app. Shown to the people the notice
 * can reach (a live session), once; the panel's "Notifications" button brings
 * it back to change the answer.
 *
 * Telegram needs a chat: after the answer, the bot's code for one of their
 * running sessions is fetched (the backend mints it for someone who chose
 * Telegram) and shown as the command to send, the same way a signal session
 * links. Until they send it, the notice falls back to email (backend
 * orb_nudge_email.go), so choosing Telegram never loses the notice.
 ****************/

type Link = { code?: string; command?: string; deep_link?: string; linked?: boolean };

export function OrbNoticeCard({ state, onChanged }: { state: OrbNoticeState; onChanged: (s: OrbNoticeState) => void }) {
  const { t } = useTranslation('common');
  const [saved, setSaved] = useState<OrbNoticeChannel | null>(null);
  const [link, setLink] = useState<Link | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'save' | 'link' | null>(null);

  const choose = async (channel: OrbNoticeChannel) => {
    setBusy(true);
    setFailed(null);
    try {
      const next = await assistantApi.setNotice(channel);
      setSaved(channel);
      onChanged(next);
      if (channel === 'telegram' && !next.telegram_linked && next.session_id) {
        try {
          setLink(await subscriptionApi.telegramLink(next.session_id));
        } catch {
          setFailed('link');
        }
      }
    } catch {
      setFailed('save');
    } finally {
      setBusy(false);
    }
  };

  const options: OrbNoticeChannel[] = ['email', 'telegram', 'none'];
  return (
    <div className="flex flex-col items-start" data-testid="orb-notice">
      <div className="rounded-lg px-3 py-2 text-sm max-w-[85%] bg-white/[0.04] text-foreground space-y-2">
        <p>{t('orb.notice.question')}</p>
        <div className="flex flex-wrap gap-1.5">
          {options.map((c) => (
            <button
              key={c}
              type="button"
              disabled={busy}
              onClick={() => void choose(c)}
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50 ${
                (saved ?? state.channel) === c
                  ? 'border-primary/50 bg-primary/[0.1] text-primary'
                  : 'border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20'
              }`}
            >
              {t(`orb.notice.${c}`)}
            </button>
          ))}
        </div>
        {saved === 'email' && <p className="text-xs text-muted-foreground">{t('orb.notice.savedEmail')}</p>}
        {saved === 'none' && <p className="text-xs text-muted-foreground">{t('orb.notice.savedNone')}</p>}
        {saved === 'telegram' && (state.telegram_linked || link?.linked) && (
          <p className="text-xs text-muted-foreground">{t('orb.notice.savedTelegram')}</p>
        )}
        {saved === 'telegram' && link && !link.linked && link.command && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{t('orb.notice.telegramSteps')}</p>
            <code className="block rounded bg-black/30 px-2 py-1 text-xs select-all">{link.command}</code>
            {link.deep_link && (
              <a href={link.deep_link} target="_blank" rel="noreferrer" className="inline-block text-xs text-primary hover:underline">
                {t('orb.notice.telegramOpen')}
              </a>
            )}
          </div>
        )}
        {failed === 'link' && <p className="text-xs text-danger">{t('orb.notice.telegramFailed')}</p>}
        {failed === 'save' && <p className="text-xs text-danger">{t('orb.notice.saveFailed')}</p>}
      </div>
    </div>
  );
}
